import { isMonthBeforeDay } from '@/helpers/date-helpers'
import DateFormatToken from '@/constants/date/DateFormatToken'
import { minutesToHoursMinsString } from '@/utils/minutes-to-hours-mins-string'
import { RateUnitType } from '@/constants/booking/RateUnitType'
import { ShortTermStatus } from '@/constants/booking/ShortTermStatus'
import dayjs from '@/services/date/index'
import TimesheetTimeStringDetailsModel from '@/models/disputes/timesheetTimeStringDetailsModel'
import { ResolveOperations } from '@/constants/disputes/ResolveOperations'
import { orderBy } from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import { TimesheetOperationTypeInts } from '@/constants/disputes/TimesheetOperationType'

export default {
  computed: {
    ...mapGetters({
      resolveStatus: 'disputes/resolveStatus',
      activeTimesheet: 'disputes/activeTimesheet',
      isLoadingDisputeSubmit: 'disputes/isLoadingDisputeSubmit',
    }),
  },
  methods: {
    ...mapActions({
      submitTimesheets: 'disputes/submitTimesheets',
    }),
    renderDate(dateUtc, timezone, format) {
      let formatString = format

      if (!formatString) {
        formatString = isMonthBeforeDay(this.$i18n.locale.toLowerCase())
          ? DateFormatToken.dddMMDDYYYYWithSlash
          : DateFormatToken.dddDDMMYYYYWithSlash
      }

      const dateTz = dateUtc.tz(timezone)
      return dateTz.format(formatString)
    },
    renderTime(dateUtc, timezone, format = DateFormatToken.HHmm) {
      return this.renderDate(dateUtc, timezone, format)
    },
    renderLocalDateToTime(date, format = DateFormatToken.HHmm) {
      return dayjs(date).format(format)
    },
    renderLocalTime(
      time,
      initialFormat = DateFormatToken.HHmm,
      newFormat = DateFormatToken.hhmma
    ) {
      return dayjs(time, DateFormatToken.HHmm).format(DateFormatToken.hhmma)
    },
    renderFullOrAbbreviatedTime(time, abbreviated) {
      return abbreviated ? time : this.renderLocalTime(time)
    },
    isHourlyOrHybridUnitType(timesheet) {
      const hourlyUnitTypes = [
        RateUnitType.HOURLY,
        RateUnitType.CLIENT_UNIT_CANDIDATE_HOURLY,
      ]
      return hourlyUnitTypes.includes(timesheet?.unitType)
    },
    getBreakMinutesText(minutes, abbreviated = false) {
      return minutesToHoursMinsString(minutes, abbreviated)
    },
    getOriginalBookingDetails(timesheet, abbreviated = false) {
      const timeFormatString = abbreviated
        ? DateFormatToken.HHmm
        : DateFormatToken.hhmma

      return new TimesheetTimeStringDetailsModel({
        startTime: this.renderLocalDateToTime(
          timesheet.startTimeLocal,
          timeFormatString
        ),
        endTime: this.renderLocalDateToTime(
          timesheet.endTimeLocal,
          timeFormatString
        ),
        payOptionType: timesheet.payOptionType,
        breaks: timesheet.breakMinutes,
        abbreviated,
      })
    },
    getModifiedBookingDetails(timesheet, abbreviated = false) {
      return new TimesheetTimeStringDetailsModel({
        startTime: this.renderFullOrAbbreviatedTime(
          timesheet.candidateBookingModifications.startTime,
          abbreviated
        ),
        endTime: this.renderFullOrAbbreviatedTime(
          timesheet.candidateBookingModifications.endTime,
          abbreviated
        ),
        payOptionType: timesheet.payOptionType,
        breaks: timesheet.candidateBookingModifications.unpaidBreakMinutes,
        abbreviated,
      })
    },
    /**
     * Returns the original booking details or the modified details, if the
     * candidate has modified the booking
     * @param {*} timesheet
     * @param {Boolean} abbreviated
     * @returns
     */
    getModifiedOrOriginalBookingDetails(timesheet, abbreviated = false) {
      const hasCandidateModifiedTimesheet =
        timesheet.bookingStatus ===
        ShortTermStatus.CANDIDATE_SUBMITTED_TIMESHEET_MODIFIED

      return hasCandidateModifiedTimesheet
        ? this.getModifiedBookingDetails(timesheet, abbreviated)
        : this.getOriginalBookingDetails(timesheet, abbreviated)
    },
    /**
     * @param {TimesheetTimeStringDetailsModel} bookingDetails
     * @returns
     */
    generateFullBookingTimeString(bookingDetails) {
      let bookingTimeString = `${bookingDetails.startTime} - ${bookingDetails.endTime}`

      bookingTimeString += `\u00A0\u00A0${bookingDetails.payOptionType}`

      bookingTimeString += `\u00A0\u00A0${this.getBreakMinutesText(
        bookingDetails.breaks,
        bookingDetails.abbreviated
      )}`

      return bookingTimeString
    },
    isDifferencePositive(differenceInMinutes) {
      return differenceInMinutes > 0
    },
    differenceInMinutesAsPositiveInt(differenceInMinutes) {
      return Math.abs(differenceInMinutes)
    },
    determineMostRecentTimesheetSubmissionOrder(timesheet) {
      const submissionsList = [
        { type: ResolveOperations.ORIGINAL, date: dayjs(timesheet.startDate) },
      ]

      if (timesheet.candidateHasSubmitted)
        submissionsList.push({
          type: ResolveOperations.CANDIDATE,
          date: dayjs(timesheet?.candidateTimesheet?.submittedDateUtc),
        })

      if (timesheet.clientHasSubmitted)
        submissionsList.push({
          type: ResolveOperations.CLIENT,
          date: dayjs(timesheet?.clientTimesheet?.submittedDateUtc),
        })

      return orderBy(submissionsList, 'date', 'desc').map(
        (submission) => submission.type
      )
    },
    bookingStatusText(bookingStatus) {
      const translationExists = this.$te(`booking.status.${bookingStatus}`)

      if (!translationExists) return bookingStatus
      return `${this.$t(`booking.status.${bookingStatus}`)} (${bookingStatus})`
    },
    async submitActiveTimesheet() {
      if (this.resolveStatus !== null) {
        await this.submitTimesheets({
          bookingId: this.activeTimesheet.bookingId,
          operation: this.resolveStatus,
        })
      }
    },
    resetResolveStatus() {
      this.$store.commit('disputes/SET_FLYOUT', {
        key: 'resolveStatus',
        value: null,
      })
    },
    isAcceptOperation(resolveStatus) {
      switch (resolveStatus) {
        case ResolveOperations.CANDIDATE:
          return (
            this.activeTimesheet.candidateTimesheet.details.operationType ===
            TimesheetOperationTypeInts.ACCEPT
          )
        case ResolveOperations.CLIENT:
          return (
            this.activeTimesheet.clientTimesheet.details.operationType ===
            TimesheetOperationTypeInts.ACCEPT
          )
        case ResolveOperations.ORIGINAL:
        default:
          return true
      }
    },
    isCancelOperation(resolveStatus) {
      switch (resolveStatus) {
        case ResolveOperations.CANDIDATE:
          return (
            this.activeTimesheet.candidateTimesheet.details.operationType ===
            TimesheetOperationTypeInts.CANCEL
          )
        case ResolveOperations.CLIENT:
          return (
            this.activeTimesheet.clientTimesheet.details.operationType ===
            TimesheetOperationTypeInts.CANCEL
          )
        case ResolveOperations.ORIGINAL:
        default:
          return false
      }
    },
    isModifiedOperation(resolveStatus) {
      switch (resolveStatus) {
        case ResolveOperations.CANDIDATE:
          return (
            this.activeTimesheet.candidateTimesheet.details.operationType ===
            TimesheetOperationTypeInts.MODIFY
          )
        case ResolveOperations.CLIENT:
          return (
            this.activeTimesheet.clientTimesheet.details.operationType ===
            TimesheetOperationTypeInts.MODIFY
          )
        case ResolveOperations.ORIGINAL:
        default:
          return false
      }
    },
    retrieveModifiedTimesheetPayload(resolveStatus) {
      if (
        !this.isModifiedOperation(resolveStatus) ||
        resolveStatus === ResolveOperations.ORIGINAL
      )
        return null

      switch (resolveStatus) {
        case ResolveOperations.CANDIDATE:
          return this.activeTimesheet.candidateTimesheet.modifications
        case ResolveOperations.CLIENT:
          return this.activeTimesheet.clientTimesheet.modifications
        default:
          return null
      }
    },
    clientHasSubmittedTimesheet(bookingStatus) {
      return ShortTermStatus.getClientSubmittedStatuses().includes(
        bookingStatus
      )
    },
    candidateHasSubmittedTimesheet(bookingStatus) {
      return ShortTermStatus.getCandidateSubmittedStatuses().includes(
        bookingStatus
      )
    },
  },
}
